const VALID_RESTAURANT_TYPES = ['casual', 'fineDining', 'cafe', 'bar', 'fastFood', 'darkKitchen'] as const;

export type RestaurantTypeValue = typeof VALID_RESTAURANT_TYPES[number];

/** Validated restaurant type value object */
export type RestaurantType = Readonly<RestaurantTypeValue>;

/**
 * Creates a validated RestaurantType value object.
 *
 * @param raw - Raw restaurant type string from input
 * @returns Validated RestaurantType
 * @throws Error if restaurant type is not recognized
 */
export const createRestaurantType = (raw: string): RestaurantType => {
  if (!VALID_RESTAURANT_TYPES.includes(raw as RestaurantTypeValue)) {
    throw new Error(`Invalid restaurant type: "${raw}". Must be one of: ${VALID_RESTAURANT_TYPES.join(', ')}`);
  }
  return raw as RestaurantType;
};

/** Maps the form option key to the API storeProfile value. */
export const STORE_PROFILE_MAP: Readonly<Record<string, string>> = {
  bowls: 'bowls_healthy',
  hamburgers: 'burgers',
  taquerias: 'taquerias',
  casualDining: 'casual_dining',
  fineDining: 'fine_dining',
  seafood: 'seafood_ceviche',
  sushi: 'sushi_asian_food',
  bar: 'bar',
  entertainment: 'entertainment',
  cafe: 'coffee_shop',
  bakery: 'bakery_ice_cream_shop',
  darkKitchen: 'dark_kitchen',
  pizzeria: 'pizzeria',
};

/** Maps the form option key to the API serviceType value. */
export const SERVICE_TYPE_MAP: Readonly<Record<string, string>> = {
  bowls: 'quick_service_counter',
  cafe: 'quick_service_counter',
  hamburgers: 'quick_service_counter',
  taquerias: 'quick_service_counter',
  casualDining: 'table_service',
  fineDining: 'table_service',
  seafood: 'table_service',
  sushi: 'table_service',
  bar: 'bar_entertainment',
  entertainment: 'bar_entertainment',
  pizzeria: 'delivery_only_specialized',
  darkKitchen: 'delivery_only_specialized',
  bakery: 'quick_service_counter',
};

/**
 * Derives the HubSpot properties (storeProfile + serviceType) from a restaurant type key.
 *
 * @param restaurantType - Form option key (e.g. 'casualDining')
 * @returns Object with storeProfile and serviceType API values
 */
export const getHubspotProperties = (restaurantType: string): { storeProfile: string; serviceType: string } => ({
  storeProfile: STORE_PROFILE_MAP[restaurantType] ?? restaurantType,
  serviceType: SERVICE_TYPE_MAP[restaurantType] ?? 'quick_service_counter',
});
